/* ==========================================================================
   rs-launch-bar.js — countdown + slide rotation + mobile marquee. BAR v2.
   SOURCE OF TRUTH: tools/seo/theme-bundle/assets/rs-launch-bar.js
   Installed by tools/seo/apply_theme_bundle.py. Never edit the theme copy.

   Loaded ONLY from inside sections/rs-launch-bar.liquid's render gate, so it
   never ships on a page that has no bar.

   v2 (founder decisions 2026-09-15)
     · rotation restored — slide A (promotion) / slide B (brand strengths)
     · desktop dwell 10s / 5s; MOBILE dwell for slide A is DERIVED, not fixed:
       it is the measured time one full marquee pass needs at the site's own
       approved reading speed, rounded up to the next whole second
     · seconds are back in the countdown and tick live
     · ONE control pauses ALL motion together: rotation, marquee, seconds tick
     · while paused the clock still refreshes once a MINUTE, so a paused bar
       never shows a stale time
     · prefers-reduced-motion starts in the paused-equivalent state

   WHY THE MOBILE DWELL IS MEASURED AND NOT A CONSTANT
     The founder approved "one full pass completes". A pass duration depends on
     the rendered width of the line, which depends on the font size, the font
     that actually loaded and the viewport. Hardcoding 26s would silently stop
     being a full pass the first time any of those change. So the width is read
     from the DOM after layout and the dwell follows it.

   WHY THE MARQUEE TRAVELS IN +X
     Hebrew renders its first character at the RIGHT end of the line box. To
     show the beginning of the sentence first, the line must enter from the
     LEFT edge and travel right — which is also the direction the site's own
     approved announcement marquee uses (rs-marq-rtl). The two never disagree.

   WHY THE MARQUEE IS RESTORED IN JS
     The Liquid gate cannot re-run in an open tab. A visitor sitting on the
     page across the end boundary would otherwise be left with no launch bar
     AND no marquee, because the marquee is hidden by a rule this section
     emitted. Removing the bar has to put the marquee back in the same frame.

   NO network calls. NO analytics events on tick or on pause.
   ========================================================================== */
(function () {
  'use strict';

  var SS_KEY = 'rs_lb_paused';
  var MOBILE_Q = '(max-width: 749px)';

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* sessionStorage is unavailable in a private window, behind blocked site
     data, and in preview/thumbnail contexts, where the accessor itself throws.
     Every access is wrapped; failure degrades to "not paused", never to an
     exception that would take the countdown down with it. */
  function ssGet() {
    try { return window.sessionStorage.getItem(SS_KEY) === '1'; }
    catch (e) { return false; }
  }
  function ssSet(v) {
    try {
      if (v) window.sessionStorage.setItem(SS_KEY, '1');
      else window.sessionStorage.removeItem(SS_KEY);
    } catch (e) { /* no-op */ }
  }

  function bind(bar) {
    if (!bar || bar.dataset.rsLbBound) return;

    var endRaw = bar.getAttribute('data-end') || '';
    var endMs = Date.parse(endRaw);

    // Date.parse returns NaN on a malformed string. Fall back to the epoch the
    // Liquid gate already computed, which is integer-safe and authoritative.
    if (isNaN(endMs)) {
      var epoch = parseInt(bar.getAttribute('data-end-epoch') || '', 10);
      // parseInt('') is NaN and 0 is the section's "unset" sentinel — neither
      // is a usable end, so leave the bar static rather than hide it.
      if (!epoch) return;
      endMs = epoch * 1000;
    }

    bar.dataset.rsLbBound = '1';

    var el = {
      d: bar.querySelector('[data-rs-lb-d]'), dl: bar.querySelector('[data-rs-lb-dl]'),
      h: bar.querySelector('[data-rs-lb-h]'), hl: bar.querySelector('[data-rs-lb-hl]'),
      m: bar.querySelector('[data-rs-lb-m]'), ml: bar.querySelector('[data-rs-lb-ml]'),
      s: bar.querySelector('[data-rs-lb-s]'), sl: bar.querySelector('[data-rs-lb-sl]')
    };
    var L = {
      day: bar.getAttribute('data-label-day') || '',
      days: bar.getAttribute('data-label-days') || '',
      hour: bar.getAttribute('data-label-hour') || '',
      hours: bar.getAttribute('data-label-hours') || '',
      min: bar.getAttribute('data-label-min') || '',
      sec: bar.getAttribute('data-label-sec') || ''
    };

    var countTimer = null;
    var rotTimer = null;

    var reduceQ = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var reduce = !!(reduceQ && reduceQ.matches);

    // TWO INDEPENDENT PAUSE REASONS, deliberately not one flag.
    //   hoverHold — transient, from hover/focus; releases by itself.
    //   userHold  — sticky, from the SC 2.2.2 control; only the user clears it.
    // Collapsing them into one boolean makes a mouseleave silently restart a
    // carousel the user explicitly stopped, which is the exact failure the
    // accessibility requirement exists to prevent.
    var hoverHold = false;
    var userHold = ssGet();

    // Reduced motion starts in the paused-equivalent state: no rotation, no
    // marquee, no per-second tick. It does NOT write sessionStorage — an OS
    // preference must not masquerade as a choice the user made in the page.
    var motionOff = function () { return userHold || reduce; };

    /* ---------------- countdown ---------------- */

    function restoreMarquee() {
      // The marquee was hidden by a rule inside this section's <style>. That
      // rule uses !important, so the override has to as well.
      var ann = document.querySelector('.rs-ann');
      if (ann) ann.style.setProperty('display', 'flex', 'important');
    }

    function finish() {
      if (countTimer) { clearInterval(countTimer); countTimer = null; }
      if (rotTimer) { clearTimeout(rotTimer); rotTimer = null; }
      bar.hidden = true;
      bar.style.display = 'none';

      // Hand the row back to the marquee, then re-point --rs-launch-h at the
      // MARQUEE's height rather than zeroing it. Zeroing it put the overlay
      // header at the very top, overlapping the marquee that had just come
      // back. Assigning the literal string `var(--rs-ann-h)` makes every
      // consumer resolve to the marquee height instead — and because custom
      // properties substitute lazily, it stays correct across a resize.
      restoreMarquee();
      document.documentElement.style.setProperty('--rs-launch-h', 'var(--rs-ann-h)');
    }

    function tick() {
      var remaining = endMs - Date.now();
      if (remaining <= 0) { finish(); return; }

      var total = Math.floor(remaining / 1000);
      var days = Math.floor(total / 86400);
      var hrs = Math.floor((total % 86400) / 3600);
      var mins = Math.floor((total % 3600) / 60);
      var secs = total % 60;

      if (el.d) el.d.textContent = String(days);
      if (el.h) el.h.textContent = pad(hrs);
      if (el.m) el.m.textContent = pad(mins);
      if (el.s) el.s.textContent = pad(secs);

      // Hebrew agreement: 1 takes the singular form, everything else plural.
      if (el.dl) el.dl.textContent = (days === 1) ? L.day : L.days;
      if (el.hl) el.hl.textContent = (hrs === 1) ? L.hour : L.hours;
      if (el.ml) el.ml.textContent = L.min;
      if (el.sl) el.sl.textContent = L.sec;

      // Reveal only once real values are in. The markup ships `hidden` so a
      // no-JS or failed-JS render shows no empty placeholders — the message
      // line carries the full deadline including the time on its own.
      var box = bar.querySelector('[data-rs-lb-count]');
      if (box && box.hidden) box.hidden = false;
    }

    // ONE timer, two cadences. Paused does not mean frozen: a bar that showed
    // "03:07" for ten minutes would be lying. It refreshes once a minute, which
    // is not "moving content" under SC 2.2.2 and is what the founder specified.
    function startClock() {
      if (countTimer) { clearInterval(countTimer); countTimer = null; }
      tick();
      if (bar.hidden) return;
      countTimer = setInterval(tick, motionOff() ? 60000 : 1000);
    }

    startClock();

    // A backgrounded tab throttles setInterval, so the clock can return stale.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && countTimer) tick();
    });

    /* ---------------- mobile marquee ---------------- */

    var win = bar.querySelector('[data-rs-lb-win]');
    var track = bar.querySelector('[data-rs-lb-track]');
    var pxs = parseFloat(bar.getAttribute('data-marquee-pxs') || '') || 42.8;
    var mq = window.matchMedia ? window.matchMedia(MOBILE_Q) : null;
    var passSec = 0;

    function isMobile() { return !!(mq && mq.matches); }

    // Measure AFTER layout and write both the distance and the duration as
    // custom properties. The CSS keyframes read the distance, so the animation
    // and the dwell are computed from the same two numbers and cannot drift.
    function measurePass() {
      if (!win || !track) return 0;
      var textW = track.scrollWidth;
      var winW = win.getBoundingClientRect().width;
      if (!textW || !winW) return 0;
      var dist = textW + winW;
      var secs = dist / pxs;
      bar.style.setProperty('--rs-lb-text', textW + 'px');
      bar.style.setProperty('--rs-lb-win', winW + 'px');
      bar.style.setProperty('--rs-lb-pass', (Math.round(secs * 100) / 100) + 's');
      // Exposed for render verification. MOBILE ONLY: on desktop the line wraps
      // instead of scrolling, so scrollWidth equals the container and the number
      // would be a meaningless artefact of the measurement rather than a pass.
      if (isMobile()) {
        bar.setAttribute('data-pass-measured', String(Math.round(secs * 100) / 100));
        bar.setAttribute('data-pass-dwell', String(Math.ceil(secs)));
      } else {
        bar.removeAttribute('data-pass-measured');
        bar.removeAttribute('data-pass-dwell');
      }
      return secs;
    }

    // "The marquee pass starts fresh each time Slide A appears. It never
    // resumes mid-line." Re-assigning animation after a forced reflow is the
    // only reliable way to restart a CSS animation from frame zero.
    function restartMarquee() {
      if (!track || !isMobile()) return;
      track.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      track.offsetWidth;
      track.style.animation = '';
    }

    /* ---------------- rotation ---------------- */

    var slides = bar.querySelectorAll('.rs-lb__slide');
    var dwellPrimary = parseInt(bar.getAttribute('data-dwell-primary') || '', 10) || 10000;
    var dwellOther = parseInt(bar.getAttribute('data-dwell-other') || '', 10) || 5000;
    var idx = 0;

    function currentDwell() {
      if (idx !== 0) return dwellOther;
      // Slide A on mobile lasts exactly as long as one full pass needs,
      // rounded UP to the next whole second so the line always completes.
      if (isMobile() && passSec > 0) return Math.ceil(passSec) * 1000;
      return dwellPrimary;
    }

    function running() { return !hoverHold && !userHold && !reduce; }

    function schedule() {
      if (rotTimer) { clearTimeout(rotTimer); rotTimer = null; }
      if (!running() || slides.length < 2) return;
      rotTimer = setTimeout(advance, currentDwell());
    }

    function advance() {
      slides[idx].classList.remove('is-active');
      idx = (idx + 1) % slides.length;
      slides[idx].classList.add('is-active');
      if (idx === 0) restartMarquee();
      schedule();
    }

    function halt() { if (rotTimer) { clearTimeout(rotTimer); rotTimer = null; } }

    // The paused class drives animation-play-state for the marquee, so pause
    // stops the scroll in the same frame it stops the rotation.
    function applyPaused() {
      if (motionOff()) bar.classList.add('rs-lb--paused');
      else bar.classList.remove('rs-lb--paused');
      startClock();
    }

    passSec = measurePass();
    applyPaused();

    var resizeT = null;
    window.addEventListener('resize', function () {
      if (resizeT) clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        passSec = measurePass();
        if (idx === 0) restartMarquee();
        schedule();
      }, 200);
    });

    if (slides.length > 1) {
      bar.addEventListener('mouseenter', function () { hoverHold = true; halt(); });
      bar.addEventListener('mouseleave', function () { hoverHold = false; schedule(); });
      // Keyboard parity: a user tabbing onto the link gets the same hold.
      bar.addEventListener('focusin', function () { hoverHold = true; halt(); });
      bar.addEventListener('focusout', function () { hoverHold = false; schedule(); });
    }

    // SC 2.2.2 (Pause, Stop, Hide) — a control presented IN THE PAGE, which is
    // what the accessibility regulations require; prefers-reduced-motion is an
    // OS preference and does not discharge the duty.
    var toggle = bar.querySelector('[data-rs-lb-toggle]');
    if (toggle) {
      var pauseLabel = bar.getAttribute('data-label-pause') || toggle.getAttribute('aria-label') || '';
      var playLabel = bar.getAttribute('data-label-play') || pauseLabel;

      function paint() {
        // ONE state mechanism, not two. Carrying a CHANGING aria-label and
        // aria-pressed together makes several screen readers announce both —
        // "המשך התנועה, לחוץ" — where the label says resume and the state says
        // pressed, which contradict each other. Flagged by brand-reviewer
        // 2026-09-15. The changing label is kept because it always states what
        // the next click will DO, and aria-pressed is dropped. The glyph hook
        // moves to data-paused so the icon still cannot disagree with the label.
        toggle.setAttribute('data-paused', userHold ? 'true' : 'false');
        var lbl = userHold ? playLabel : pauseLabel;
        toggle.setAttribute('aria-label', lbl);
        toggle.setAttribute('title', lbl);
      }
      paint();

      toggle.addEventListener('click', function () {
        userHold = !userHold;
        ssSet(userHold);
        paint();
        applyPaused();
        if (userHold) { halt(); } else { schedule(); }
      });
    }

    if (!reduce) schedule();
    if (!motionOff()) restartMarquee();
  }

  function init() {
    var bars = document.querySelectorAll('[data-rs-launch-bar]');
    for (var i = 0; i < bars.length; i++) bind(bars[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders a section in place without a page load.
  document.addEventListener('shopify:section:load', init);
})();
